import { useCallback, useMemo, useState } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Request } from 'commons/utils/request/request'
import { isEqual } from 'lodash'
import { useConfirmation } from 'ui/hooks'
import { Batch as BatchHook } from '~bank-bsa-reporting/hooks'

import { SARListForm, SARListItem, SARListWebPage } from './types'

const defaultFilter = (): SARListForm => ({
  batchId: null,
  bsaTrackingId: null,
  date: null,
  status: null,
  page: 1,
  size: 10
})

export const useData = () => {
  const deleteMutation = BatchHook.useDelete()
  const [sarItemForInfoView, setSarItemForInfoView] = useState<SARListItem | null>(null)
  const [sarItemToDelete, setSarItemToDelete] = useState<SARListItem | null>(null)
  const [filter, setFilter] = useState<SARListForm>(defaultFilter())

  const onFilterChange = useCallback(
    (value: Partial<SARListForm>) => {
      if (!isEqual(filter, value)) {
        setFilter({ ...filter, ...value })
      }
    },
    [filter]
  )

  const { data, isFetching, refetch } = useQuery<SARListWebPage>({
    queryKey: ['bsa-batch-details', filter],
    queryFn: () => Request.post(Request.urls.bank.sarBatchListWebPage, filter),
    structuralSharing: true,
    staleTime: 1000,
    placeholderData: keepPreviousData
  })

  const onClear = useCallback(() => {
    setFilter(defaultFilter)
  }, [])

  const tableConfig = useMemo(
    () => ({
      isFetching,
      onFilterChange,
      onClear
    }),
    [isFetching, onClear, onFilterChange]
  )

  const [chosenSarItems, setChosenSarItems] = useState<SARListItem[]>([])

  const { open: startDeleting, Confirmation: DeleteConfirmation } = useConfirmation({
    message: (
      <span>
        Are you sure you want to delete{' '}
        <i>{sarItemToDelete || chosenSarItems.length === 1 ? (sarItemToDelete || chosenSarItems[0]).id : 'these'}</i>
        &nbsp;Batch(es)?
      </span>
    ),
    onConfirm: () =>
      deleteMutation.mutateAsync(sarItemToDelete ? [sarItemToDelete.id] : chosenSarItems.map((i) => i.id)).then(() => {
        setChosenSarItems([])
        setSarItemToDelete(null)
        refetch()
      }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const handleSarItemSelect = (item?: SARListItem) => {
    if (!item) {
      if (data?.items && chosenSarItems.length < data?.items.length) {
        setChosenSarItems(data?.items)
      } else {
        setChosenSarItems([])
      }
    } else {
      const index = chosenSarItems.findIndex((i) => i.id === item.id)
      if (index === -1) {
        setChosenSarItems(chosenSarItems.concat(item))
      } else {
        const copy = [...chosenSarItems]
        copy.splice(index, 1)
        setChosenSarItems(copy)
      }
    }
  }

  const handleDeleteFromTableMenu = (item: SARListItem) => {
    setSarItemToDelete(item)
    startDeleting()
  }

  const onPageChange = useCallback(
    (page: number, size: number) => onFilterChange({ ...data?.form, page, size }),
    [onFilterChange, data?.form]
  )

  return {
    isFetching,
    data,
    sarItemForInfoView,
    chosenSarItems,
    DeleteConfirmation,
    setSarItemForInfoView,
    startDeleting,
    handleSarItemSelect,
    handleDeleteFromTableMenu,
    tableConfig,
    onPageChange
  }
}
